import { getRoleLabel } from "./roleVariant";
import { personCosts, resourceCosts } from "../../../utils/costUtils";
import { sortByAccessor } from "../../../utils/sortRows";

export const PEOPLE_COLUMNS = [
  { key: "name", label: "Name", visible: true },
  { key: "role", label: "Role", visible: true },
  { key: "email", label: "Email", visible: true },
  { key: "phone", label: "Phone", visible: false },
  { key: "department", label: "Department", visible: false },
  { key: "week", label: "This week", visible: true },
  { key: "capacity", label: "Capacity", visible: false },
  { key: "cost_rate", label: "Cost rate", visible: false },
  { key: "profit", label: "Profit / mo", visible: true },
  { key: "leave_left", label: "Leave left", visible: false },
  { key: "projects", label: "Projects", visible: false },
  { key: "documents", label: "Documents", visible: false },
];

export const RESOURCE_COLUMNS = [
  { key: "name", label: "Name", visible: true },
  { key: "type", label: "Type", visible: true },
  { key: "status", label: "Status", visible: true },
  { key: "assigned", label: "Assigned to", visible: true },
  { key: "profit", label: "Profit / mo", visible: true },
  { key: "reference", label: "Reference", visible: false },
  { key: "cost_center", label: "Cost centre", visible: false },
  { key: "next_booking", label: "Next booking", visible: false },
  { key: "notes", label: "Notes", visible: false },
  { key: "documents", label: "Documents", visible: false },
];

// Hidden from anyone without view_project_budgets.
export const FINANCE_KEYS = new Set(["cost_rate", "profit"]);

export const RESOURCE_TYPES = [
  { value: "vehicle", label: "Vehicle" },
  { value: "tool", label: "Tool / Equipment" },
  { value: "misc", label: "Miscellaneous" },
];

export const RESOURCE_STATUSES = [
  { value: "available", label: "Available", color: "#16a34a" },
  { value: "in_use", label: "In use", color: "#2563eb" },
  { value: "maintenance", label: "Maintenance", color: "#d97706" },
  { value: "unavailable", label: "Unavailable", color: "#dc2626" },
];

export const resourceTypeLabel = (type) => RESOURCE_TYPES.find((t) => t.value === type)?.label || type;
export const resourceStatusLabel = (s) => RESOURCE_STATUSES.find((x) => x.value === s)?.label || s;
export const resourceStatusColor = (s) => RESOURCE_STATUSES.find((x) => x.value === s)?.color || "#6b7280";

export const isInvite = (p) => p.status === "invited";
export const personRowId = (p) => (isInvite(p) ? `inv-${p.invite_id}` : `emp-${p.user_id}`);
export const personRoleLabel = (p) => p.role_name || getRoleLabel(p);
export const personKind = (p) => (isInvite(p) ? "Invited" : p.kind === "ghost" ? "Contractors" : "Employees");

// Group by options per tab: label for the dropdown, how a row maps to a group, and the group order / dot colour.
export const PEOPLE_GROUPS = {
  kind: {
    label: "Type",
    get: personKind,
    order: ["Employees", "Contractors", "Invited"],
    color: { Employees: "#185FA5", Contractors: "#b45309", Invited: "#94a3b8" },
  },
  role: { label: "Role", get: personRoleLabel },
  department: { label: "Department", get: (p) => p.department || "No department" },
};

export const RESOURCE_GROUPS = {
  type: {
    label: "Type",
    get: (r) => resourceTypeLabel(r.type),
    order: RESOURCE_TYPES.map((t) => t.label),
    color: { Vehicle: "#185FA5", "Tool / Equipment": "#c2410c", Miscellaneous: "#b45309" },
  },
  status: {
    label: "Status",
    get: (r) => resourceStatusLabel(r.availability_status),
    order: RESOURCE_STATUSES.map((s) => s.label),
    color: Object.fromEntries(RESOURCE_STATUSES.map((s) => [s.label, s.color])),
  },
};

export const DEFAULT_FILTERS = {
  people: { search: "", role: "", department: "", groupBy: "kind", archived: false, sort: null },
  resources: { search: "", type: "", status: "", groupBy: "type", archived: false, sort: null },
};

const includes = (text, q) => (text || "").toString().toLowerCase().includes(q);

export const filterPeople = (rows, f) => {
  const q = f.search.trim().toLowerCase();
  return rows.filter((p) =>
    (isInvite(p) ? !f.archived : (p.is_active === false) === f.archived)
    && (!f.role || personRoleLabel(p) === f.role)
    && (!f.department || (p.department || "") === f.department)
    && (!q || [p.name, p.email, p.phone_number, p.job_title, personRoleLabel(p), p.department].some((v) => includes(v, q)))
  );
};

export const filterResources = (rows, f) => {
  const q = f.search.trim().toLowerCase();
  return rows.filter((r) =>
    (r.is_active === false) === f.archived
    && (!f.type || r.type === f.type)
    && (!f.status || r.availability_status === f.status)
    && (!q || [r.name, resourceTypeLabel(r.type), r.registration_number, r.cost_center, r.holder_name, r.description].some((v) => includes(v, q)))
  );
};

const PERSON_SORT = {
  name: (p) => p.name,
  role: personRoleLabel,
  email: (p) => p.email,
  phone: (p) => p.phone_number,
  department: (p) => p.department,
  week: (p) => Number(p.week_booked_hours) || 0,
  capacity: (p) => Number(p.default_weekly_hours) || 0,
  cost_rate: (p) => Number(p.default_hourly_rate) || 0,
  profit: (p) => personCosts(p).profit,
  leave_left: (p) => Number(p.leave_left) || 0,
  projects: (p) => Number(p.project_count) || 0,
  documents: (p) => (Number(p.doc_expired) || 0) * 1000 + (Number(p.doc_expiring) || 0),
};

const RESOURCE_SORT = {
  name: (r) => r.name,
  type: (r) => resourceTypeLabel(r.type),
  status: (r) => resourceStatusLabel(r.availability_status),
  assigned: (r) => r.holder_name,
  profit: (r) => resourceCosts(r).profit,
  reference: (r) => r.registration_number,
  cost_center: (r) => r.cost_center,
  next_booking: (r) => r.next_booking,
  notes: (r) => r.description,
  documents: (r) => (Number(r.doc_expired) || 0) * 1000 + (Number(r.doc_expiring) || 0),
};

export const sortRows = (rows, sort, tab, customAccessor) => {
  if (!sort) return rows;
  return sortByAccessor(rows, sort, (tab === "people" ? PERSON_SORT : RESOURCE_SORT)[sort.key] || customAccessor(sort.key));
};
